/**
 * Get the ordinal suffix for a given day
 * @param {number} day - The day of the month
 * @returns {string} The ordinal suffix
 */

export const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
};

/**
 * Formats a date string into a readable format
 * @param {string} dateString - The date string to format
 * @param {boolean} showDayOfWeek - Whether to show the day of the week
 * @returns {string} The formatted date string
 */

export const formatDate = (
    dateString: string | Date,
    showDayOfWeek: boolean = false,
): string => {
    const date = new Date(dateString);
    const day = date.getDate();
    const dayOfWeekStr = showDayOfWeek
        ? `${date.toLocaleDateString('en-GB', { weekday: 'short' })}, `
        : '';
    const month = date.toLocaleDateString('en-GB', { month: 'short' });
    return `${dayOfWeekStr}${day}${getOrdinalSuffix(day)} ${month}`;
};

/**
 * Normalizes a date string or Date object to YYYY-MM-DD format
 * @param {string} date - The date string to normalize
 * @returns {string} The normalized date string
 */
export const normalizeDateToYYYYMMDD = (date: string | Date) => {
    if (!date) return date;
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
    }
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Gives relative time (eg. 2 seconds ago or in 3 days)
 * @param {number} current - The current time in milliseconds (unix timestamp)
 * @param {number} previous - The previous time in milliseconds (unix timestamp)
 * @returns {string} The relative time
 */
export const relativeTime = (current: number, previous: number): string => {
    const msPerMinute = 60 * 1000;
    const msPerHour = msPerMinute * 60;
    const msPerDay = msPerHour * 24;
    const msPerMonth = msPerDay * 30;
    const msPerYear = msPerDay * 365;

    const elapsed = current - previous;

    if (elapsed < 0) {
        const absElapsed = Math.abs(elapsed);

        if (absElapsed < msPerMinute) {
            const relTime = Math.round(absElapsed / 1000);
            return `in ${relTime} second${relTime === 1 ? '' : 's'}`;
        } else if (absElapsed < msPerHour) {
            const relTime = Math.round(absElapsed / msPerMinute);
            return `in ${relTime} minute${relTime === 1 ? '' : 's'}`;
        } else if (absElapsed < msPerDay) {
            const relTime = Math.round(absElapsed / msPerHour);
            return `in ${relTime} hour${relTime === 1 ? '' : 's'}`;
        } else if (absElapsed < msPerMonth) {
            const relTime = Math.round(absElapsed / msPerDay);
            return `in ${relTime} day${relTime === 1 ? '' : 's'}`;
        } else if (absElapsed < msPerYear) {
            const relTime = Math.round(absElapsed / msPerMonth);
            return `in ${relTime} month${relTime === 1 ? '' : 's'}`;
        } else {
            const relTime = Math.round(absElapsed / msPerYear);
            return `in ${relTime} year${relTime === 1 ? '' : 's'}`;
        }
    } else {
        if (elapsed < msPerMinute) {
            const relTime = Math.round(elapsed / 1000);
            if (relTime < 5) {
                return 'just now';
            }
            return `${relTime} second${relTime === 1 ? '' : 's'} ago`;
        } else if (elapsed < msPerHour) {
            const relTime = Math.round(elapsed / msPerMinute);
            return `${relTime} minute${relTime === 1 ? '' : 's'} ago`;
        } else if (elapsed < msPerDay) {
            const relTime = Math.round(elapsed / msPerHour);
            return `${relTime} hour${relTime === 1 ? '' : 's'} ago`;
        } else if (elapsed < msPerMonth) {
            const relTime = Math.round(elapsed / msPerDay);
            return `${relTime} day${relTime === 1 ? '' : 's'} ago`;
        } else if (elapsed < msPerYear) {
            const relTime = Math.round(elapsed / msPerMonth);
            return `${relTime} month${relTime === 1 ? '' : 's'} ago`;
        } else {
            const relTime = Math.round(elapsed / msPerYear);
            return `${relTime} year${relTime === 1 ? '' : 's'} ago`;
        }
    }
};

/**
 * Gives relative time from a date string or Date object
 * @param {string | Date} date - The date string or Date object
 * @returns {string} The relative time
 */
export const getRelativeTimeFromDate = (date: string | Date): string => {
    const current = new Date();
    const previous = new Date(date);
    return relativeTime(current.getTime(), previous.getTime());
};
